'use client';

import { NativeSelect } from './native-select';
import React, { useState, useMemo, useCallback, useRef } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { cn } from '../lib/utils';
import { Table } from './table';
import { EmptyState } from './empty-state';
import { TableScrollRegion } from './table-scroll-region';
import { TechnicalValue } from './technical-value';
import { Button } from './button';
import { DENSITY_ROW_CLASSES, type DataGridDensity } from './data-grid/data-grid-column-controls';

export interface DataTableColumn<T> {
  key: string;
  header: string;
  sortable?: boolean;
  locked?: boolean;
  render?: (value: unknown, row: T, index: number) => React.ReactNode;
  className?: string;
  minWidth?: React.CSSProperties['minWidth'];
  /** Opt-in technical columns start hidden and stay bounded when shown. */
  technical?: boolean;
}

export interface BulkAction {
  label: string;
  onClick: () => void;
  variant?: 'default' | 'destructive';
}

export type DataTableMobileCardBreakpoint = 'md' | 'lg' | 'xl' | 'always';

const mobileCardBreakpointClasses: Record<DataTableMobileCardBreakpoint, string> = {
  md: 'md:hidden',
  lg: 'lg:hidden',
  xl: 'xl:hidden',
  always: '',
};

const tableBreakpointClasses: Record<DataTableMobileCardBreakpoint, string> = {
  md: 'hidden md:block',
  lg: 'hidden lg:block',
  xl: 'hidden xl:block',
  always: 'hidden',
};

export interface BulkActionBarProps {
  selectedCount: number;
  actions: BulkAction[];
  onClear: () => void;
}

export function BulkActionBar({ selectedCount, actions, onClear }: BulkActionBarProps) {
  if (selectedCount === 0) return null;
  return (
    <div className="experience-candidate-data-grid flex flex-wrap items-center gap-2 border-b border-border bg-selected px-4 py-2.5">
      <span className="shrink-0 font-body text-body-sm font-semibold text-statura-green-accessible">
        {selectedCount} selected
      </span>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
        {actions.map((action) => (
          <button
            key={action.label}
            type="button"
            onClick={action.onClick}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-control px-3 py-1.5 font-body text-caption font-semibold transition-interactive duration-200',
              action.variant === 'destructive'
                ? 'bg-status-danger-fg text-text-inverse hover:bg-status-critical-fg'
                : 'bg-action text-action-foreground hover:bg-action-hover',
            )}
          >
            {action.label}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={onClear}
        className="inline-flex items-center gap-1 font-body text-caption text-text-muted transition-interactive duration-200 hover:text-text-body sm:ml-auto"
      >
        <X className="h-3 w-3" />
        Clear selection
      </button>
    </div>
  );
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  /** Presentation density; legacy names remain supported. */
  density?: DataGridDensity;
  data: T[];
  pageSize?: number;
  /** Caller-owned page of server results. Omit for the existing client pager. */
  pagination?: DataTableServerPagination;
  className?: string;
  emptyMessage?: string;
  onRowClick?: (row: T, index: number) => void;
  rowClassName?: string | ((row: T, index: number) => string);
  /** Optional per-row `data-testid` (e.g. for E2E row targeting). */
  rowTestId?: string | ((row: T, index: number) => string);
  stickyHeader?: boolean;
  /** Optional: map of column key -> visible. Columns missing from the map default to visible. */
  columnVisibility?: Record<string, boolean>;
  /** Toolbar content rendered above the table (e.g., column visibility dropdown) */
  toolbar?: React.ReactNode;
  /** Enable row selection checkboxes */
  selectable?: boolean;
  /** Unselectable rows have no checkbox and are excluded from Select all. */
  isRowSelectable?: (row: T) => boolean;
  /** Controlled set of selected row keys */
  selectedRows?: Set<string>;
  /** Called when selection changes */
  onSelectionChange?: (selected: Set<string>) => void;
  /** Function to extract a unique key from a row. Defaults to (row as any).id */
  rowKey?: (row: T) => string;
  /** Exact fixture/data revision for provider-runtime row evidence. */
  experienceEvidenceRevision?: string;
  /** Bulk actions shown when rows are selected */
  bulkActions?: BulkAction[];
  /** Optional compact row renderer used below the configured breakpoint instead of the full table. */
  mobileCardRenderer?: (row: T, index: number) => React.ReactNode;
  /** Breakpoint where the full table replaces mobile cards. Defaults to lg for dashboard sidebars. */
  mobileCardBreakpoint?: DataTableMobileCardBreakpoint;
  /**
   * Phase F1 Sprint 2 — controlled sort state. When supplied alongside
   * `onSortChange`, DataTable switches to controlled mode: it emits
   * sort changes via the callback and skips its internal client-side
   * sort (consumer is expected to re-fetch / re-order data server-side).
   * Backwards-compatible — absent props leave the existing internal
   * sort behaviour untouched.
   */
  /** Controlled client state for URL-backed registers. Omit to retain internal paging. */
  clientPage?: number;
  onClientPageChange?: (page: number) => void;
  /** Controlled sort defaults to caller/server sorting; client opts into local sorting. */
  sortMode?: 'client' | 'server';
  sort?: SortState;
  onSortChange?: (next: SortState) => void;
  /**
   * Phase F1 Sprint 2 — page-size selector. When supplied, the footer
   * renders a `<select>` over these options + `onPageSizeChange`.
   * `pageSize` remains the active size; `onPageSizeChange` is fired
   * with the new value so the consumer can persist it (typically via
   * useLayoutPreferences).
   */
  pageSizeOptions?: ReadonlyArray<number>;
  /** Entity nouns for the visible result count. */
  itemLabel?: { singular: string; plural: string };
  onPageSizeChange?: (size: number) => void;
}

export interface DataTableServerPagination {
  mode: 'server';
  page: number;
  pageSize: number;
  total?: number;
  hasMore: boolean;
  onPageChange: (page: number) => void;
  loading?: boolean;
}

export type SortDirection = 'asc' | 'desc' | null;

export interface SortState {
  key: string | null;
  direction: SortDirection;
}

function getNestedValue(obj: unknown, path: string): unknown {
  return path.split('.').reduce((acc: unknown, part: string) => {
    if (acc && typeof acc === 'object' && part in (acc as Record<string, unknown>)) {
      return (acc as Record<string, unknown>)[part];
    }
    return undefined;
  }, obj);
}

function defaultRowKey<T>(row: T): string {
  return (row as Record<string, unknown>).id as string;
}

export function DataTable<T extends Record<string, unknown>>({
  columns,
  density = 'comfortable',
  data,
  pageSize = 10,
  pagination,
  className,
  emptyMessage = 'No data available',
  onRowClick,
  rowClassName,
  rowTestId,
  stickyHeader = false,
  columnVisibility,
  toolbar,
  selectable = false,
  isRowSelectable,
  selectedRows,
  onSelectionChange,
  rowKey = defaultRowKey,
  experienceEvidenceRevision,
  bulkActions = [],
  mobileCardRenderer,
  mobileCardBreakpoint = 'lg',
  sort: controlledSort,
  onSortChange,
  clientPage,
  onClientPageChange,
  sortMode = 'server',
  pageSizeOptions,
  itemLabel = { singular: 'row', plural: 'rows' },
  onPageSizeChange,
}: DataTableProps<T>) {
  const [internalSort, setInternalSort] = useState<SortState>({ key: null, direction: null });
  // Phase F1 Sprint 2 — sort is controlled when BOTH `sort` prop and
  // `onSortChange` callback are provided. Without both, client mode keeps
  // internal sorting while server-mode headers are unavailable.
  const isSortControlled = controlledSort !== undefined && onSortChange !== undefined;
  const sort: SortState = isSortControlled ? controlledSort! : internalSort;
  const serverSortEnabled = !pagination || isSortControlled;
  const serverPage = Math.max(1, pagination?.page ?? 1);

  // Phase F1 S2 pass 1 P1 fix: `handleSort` previously closed over a
  // render-local `setSort` with `[]` deps, which captured stale `sort`
  // state and broke the asc → desc → clear cycle on repeated clicks.
  // Compute the next state from CURRENT `sort` inline + read controlled
  // mode from a ref to keep the callback dep-stable.
  const sortRef = useRef(sort);
  sortRef.current = sort;
  const isSortControlledRef = useRef(isSortControlled);
  isSortControlledRef.current = isSortControlled;
  const onSortChangeRef = useRef(onSortChange);
  onSortChangeRef.current = onSortChange;

  const [internalPage, setInternalPage] = useState(1);
  const isPageControlled = clientPage !== undefined && onClientPageChange !== undefined;
  const currentPage = Math.min(
    Math.max(1, isPageControlled ? clientPage : internalPage),
    Math.max(1, Math.ceil(data.length / pageSize)),
  );
  const setCurrentPage = useCallback(
    (page: number) => {
      if (isPageControlled) onClientPageChange(page);
      else setInternalPage(page);
    },
    [isPageControlled, onClientPageChange],
  );

  const handleSort = useCallback(
    (key: string) => {
      const prev = sortRef.current;
      const next: SortState =
        prev.key !== key
          ? { key, direction: 'asc' }
          : prev.direction === 'asc'
            ? { key, direction: 'desc' }
            : prev.direction === 'desc'
              ? { key: null, direction: null }
              : { key, direction: 'asc' };
      if (isSortControlledRef.current && onSortChangeRef.current) {
        onSortChangeRef.current(next);
      } else {
        setInternalSort(next);
      }
      setCurrentPage(1);
    },
    [setCurrentPage],
  );

  // Filter columns by visibility (if provided)
  const visibleColumns = useMemo(() => {
    return columns.filter((col) => col.locked || (columnVisibility?.[col.key] ?? !col.technical));
  }, [columns, columnVisibility]);

  const sortedData = useMemo(() => {
    // Phase F1 Sprint 2 — when sort is controlled, trust the caller's data
    // (server already sorted it). The headers still show the sort indicator
    // via `sort` state, but DataTable skips its local sort pass.
    if (pagination || (isSortControlled && sortMode !== 'client')) return data;
    if (!sort.key || !sort.direction) return data;
    return [...data].sort((a, b) => {
      const aVal = getNestedValue(a, sort.key!);
      const bVal = getNestedValue(b, sort.key!);
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        // BUG-058: Detect ISO date strings and sort numerically instead of alphabetically
        const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}/;
        if (ISO_DATE_RE.test(aVal) && ISO_DATE_RE.test(bVal)) {
          const diff = new Date(aVal).getTime() - new Date(bVal).getTime();
          return sort.direction === 'asc' ? diff : -diff;
        }
        const cmp = aVal.localeCompare(bVal);
        return sort.direction === 'asc' ? cmp : -cmp;
      }
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sort.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      const cmp = String(aVal).localeCompare(String(bVal));
      return sort.direction === 'asc' ? cmp : -cmp;
    });
  }, [data, sort, isSortControlled, pagination, sortMode]);

  const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize));
  const paginatedData = useMemo(() => {
    if (pagination) return sortedData;
    const start = (currentPage - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, currentPage, pageSize, pagination]);
  const rowOffset =
    ((pagination ? serverPage : currentPage) - 1) * (pagination?.pageSize ?? pageSize);

  // Selection helpers
  const selection = selectedRows ?? new Set<string>();
  const paginatedKeys = useMemo(
    () =>
      paginatedData
        .filter((row) => !isRowSelectable || isRowSelectable(row))
        .map((row) => rowKey(row)),
    [paginatedData, rowKey, isRowSelectable],
  );
  const allPageSelected = paginatedKeys.length > 0 && paginatedKeys.every((k) => selection.has(k));
  const somePageSelected = !allPageSelected && paginatedKeys.some((k) => selection.has(k));

  const handleToggleAll = useCallback(() => {
    if (!onSelectionChange) return;
    const next = new Set(selection);
    if (allPageSelected) {
      paginatedKeys.forEach((k) => next.delete(k));
    } else {
      paginatedKeys.forEach((k) => next.add(k));
    }
    onSelectionChange(next);
  }, [onSelectionChange, selection, allPageSelected, paginatedKeys]);

  const handleToggleRow = useCallback(
    (key: string) => {
      if (!onSelectionChange) return;
      const next = new Set(selection);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      onSelectionChange(next);
    },
    [onSelectionChange, selection],
  );

  const handleClearSelection = useCallback(() => {
    if (onSelectionChange) onSelectionChange(new Set());
  }, [onSelectionChange]);

  const SortIcon = ({ columnKey }: { columnKey: string }) => {
    if (sort.key !== columnKey) {
      return (
        <ChevronsUpDown aria-hidden="true" className="ml-1 inline h-3.5 w-3.5 text-text-subtle" />
      );
    }
    return sort.direction === 'asc' ? (
      <ChevronUp
        aria-hidden="true"
        className="ml-1 inline h-3.5 w-3.5 text-statura-green-accessible"
      />
    ) : (
      <ChevronDown
        aria-hidden="true"
        className="ml-1 inline h-3.5 w-3.5 text-statura-green-accessible"
      />
    );
  };

  const handleKeyDown = useCallback((e: React.KeyboardEvent, callback: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      callback();
    }
  }, []);

  const totalColSpan = visibleColumns.length + (selectable ? 1 : 0);

  return (
    <div
      className={cn(
        'experience-candidate-data-grid w-full min-w-0 overflow-hidden rounded-card border border-border bg-surface-sheet',
        className,
      )}
    >
      {/* Toolbar area (e.g., column visibility dropdown) */}
      {toolbar && (
        <div className="flex flex-wrap items-center justify-end gap-2 border-b border-border bg-surface-sheet px-4 py-2">
          {toolbar}
        </div>
      )}

      {/* Bulk action bar */}
      {selectable && (
        <BulkActionBar
          selectedCount={selection.size}
          actions={bulkActions}
          onClear={handleClearSelection}
        />
      )}

      {mobileCardRenderer && (
        <div className={mobileCardBreakpointClasses[mobileCardBreakpoint]} data-mobile-cards="true">
          {paginatedData.length === 0 ? (
            <EmptyState variant="compact" message={emptyMessage} />
          ) : (
            <div className="divide-y divide-border">
              {paginatedData.map((row, rowIndex) => {
                const globalIndex = rowOffset + rowIndex;
                const key = rowKey(row);
                const isSelected = selection.has(key);
                const rowCls =
                  typeof rowClassName === 'function'
                    ? rowClassName(row, globalIndex)
                    : rowClassName;
                const testId =
                  typeof rowTestId === 'function' ? rowTestId(row, globalIndex) : rowTestId;
                const experienceEvidence = experienceRowEvidence(row, key);

                return (
                  <div
                    key={key || rowIndex}
                    data-testid={testId ? `${testId}-mobile` : undefined}
                    data-experience-entity-ids={
                      experienceEvidenceRevision
                        ? experienceEvidence.entityIds.join('|') || undefined
                        : undefined
                    }
                    data-experience-outcomes={
                      experienceEvidenceRevision
                        ? experienceEvidence.outcomeTokens.join('|') || undefined
                        : undefined
                    }
                    data-experience-evidence-revision={experienceEvidenceRevision}
                    className={cn(
                      'px-4 py-3 transition-interactive duration-200',
                      onRowClick &&
                        'cursor-pointer hover:bg-surface-soft focus-visible:bg-surface-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-statura-green-accessible',
                      selectable && isSelected && 'bg-selected',
                      rowCls,
                    )}
                    onClick={onRowClick ? () => onRowClick(row, globalIndex) : undefined}
                    onKeyDown={
                      onRowClick
                        ? (e) => handleKeyDown(e, () => onRowClick(row, globalIndex))
                        : undefined
                    }
                    tabIndex={onRowClick ? 0 : undefined}
                    role={onRowClick ? 'button' : undefined}
                  >
                    <div className="flex items-start gap-3">
                      {selectable && (!isRowSelectable || isRowSelectable(row)) && (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleRow(key)}
                          onClick={(e) => e.stopPropagation()}
                          className="mt-1 h-4 w-4 cursor-pointer rounded border-control-border text-statura-green-accessible accent-statura-green-accessible focus:ring-statura-green-accessible/30"
                          aria-label={`Select row ${globalIndex + 1}`}
                        />
                      )}
                      <div className="min-w-0 flex-1">{mobileCardRenderer(row, globalIndex)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <TableScrollRegion
        className={cn(
          'max-w-full overflow-x-auto',
          stickyHeader && 'max-h-96 overflow-auto overscroll-contain',
          mobileCardRenderer && tableBreakpointClasses[mobileCardBreakpoint],
        )}
      >
        <Table
          role="table"
          data-v2-table="true"
          className={cn(
            'statura-data-table w-full min-w-full border-collapse font-body text-body tabular-nums',
            DENSITY_ROW_CLASSES[density],
          )}
        >
          <thead>
            <tr className={cn('border-b border-border bg-surface-soft')}>
              {selectable && (
                <th
                  className={cn(
                    'w-10 bg-surface-soft px-3 py-2.5 text-center',
                    stickyHeader && 'sticky top-0 z-20',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={allPageSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = somePageSelected;
                    }}
                    onChange={handleToggleAll}
                    className="h-4 w-4 cursor-pointer rounded border-control-border text-statura-green-accessible accent-statura-green-accessible focus:ring-statura-green-accessible/30"
                    aria-label="Select all rows on this page"
                  />
                </th>
              )}
              {visibleColumns.map((col, columnIndex) => (
                <th
                  key={col.key}
                  data-pinned-actions={col.key === 'actions' || col.key === 'action' || undefined}
                  style={{ minWidth: col.minWidth }}
                  className={cn(
                    'bg-surface-soft px-3 py-2.5 text-left align-top font-body text-caption font-semibold normal-case tracking-normal text-text-muted sm:px-4',
                    stickyHeader && 'sticky top-0 z-20',
                    col.sortable &&
                      serverSortEnabled &&
                      'cursor-pointer select-none transition-interactive duration-200 hover:text-text-heading',
                    data.some((row) => typeof getNestedValue(row, col.key) === 'number') &&
                      'text-right',
                    columnIndex === 0 &&
                      (selectable ? 'sticky left-10 z-20' : 'sticky left-0 z-20'),
                    (col.key === 'actions' || col.key === 'action') &&
                      'sticky right-0 z-20 bg-surface-soft',
                    col.className,
                    col.technical && 'w-48 min-w-48 max-w-48',
                    'font-body',
                  )}
                  onClick={
                    col.sortable && serverSortEnabled ? () => handleSort(col.key) : undefined
                  }
                  onKeyDown={
                    col.sortable && serverSortEnabled
                      ? (e) => handleKeyDown(e, () => handleSort(col.key))
                      : undefined
                  }
                  tabIndex={col.sortable && serverSortEnabled ? 0 : undefined}
                  role={col.sortable && serverSortEnabled ? 'button' : undefined}
                  aria-sort={
                    serverSortEnabled && sort.key === col.key
                      ? sort.direction === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : undefined
                  }
                >
                  <span className="flex min-w-0 items-start gap-1">
                    <span className="min-w-0 break-words [overflow-wrap:anywhere]">
                      {col.header}
                    </span>
                    {col.sortable && serverSortEnabled && <SortIcon columnKey={col.key} />}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.length === 0 ? (
              <tr>
                <td
                  colSpan={totalColSpan}
                  className="px-4 py-12 text-center font-body text-body-sm text-text-muted"
                >
                  <EmptyState variant="compact" message={emptyMessage} />
                </td>
              </tr>
            ) : (
              paginatedData.map((row, rowIndex) => {
                const globalIndex = rowOffset + rowIndex;
                const key = rowKey(row);
                const isSelected = selection.has(key);
                const rowCls =
                  typeof rowClassName === 'function'
                    ? rowClassName(row, globalIndex)
                    : rowClassName;
                const testId =
                  typeof rowTestId === 'function' ? rowTestId(row, globalIndex) : rowTestId;
                const experienceEvidence = experienceRowEvidence(row, key);
                return (
                  <tr
                    key={key ?? rowIndex}
                    data-testid={testId}
                    aria-selected={selectable ? isSelected : undefined}
                    data-experience-entity-ids={
                      experienceEvidenceRevision
                        ? experienceEvidence.entityIds.join('|') || undefined
                        : undefined
                    }
                    data-experience-outcomes={
                      experienceEvidenceRevision
                        ? experienceEvidence.outcomeTokens.join('|') || undefined
                        : undefined
                    }
                    data-experience-evidence-revision={experienceEvidenceRevision}
                    className={cn(
                      'border-b border-border bg-surface-sheet transition-interactive duration-200 last:border-0 hover:bg-surface-soft',
                      onRowClick &&
                        'cursor-pointer hover:bg-surface-soft focus-visible:bg-surface-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-statura-green-accessible',
                      selectable && isSelected && 'bg-selected',
                      rowCls,
                    )}
                    onClick={onRowClick ? () => onRowClick(row, globalIndex) : undefined}
                    onKeyDown={
                      onRowClick
                        ? (e) => handleKeyDown(e, () => onRowClick(row, globalIndex))
                        : undefined
                    }
                    tabIndex={onRowClick ? 0 : undefined}
                    role={onRowClick ? 'button' : undefined}
                  >
                    {selectable && (
                      <td className="w-10 h-11 px-3 py-2 text-center">
                        {(!isRowSelectable || isRowSelectable(row)) && (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleRow(key)}
                            onClick={(e) => e.stopPropagation()}
                            className="h-4 w-4 cursor-pointer rounded border-control-border text-statura-green-accessible accent-statura-green-accessible focus:ring-statura-green-accessible/30"
                            aria-label={`Select row ${globalIndex + 1}`}
                          />
                        )}
                      </td>
                    )}
                    {visibleColumns.map((col, columnIndex) => (
                      <td
                        key={col.key}
                        data-pinned-actions={
                          col.key === 'actions' || col.key === 'action' || undefined
                        }
                        style={{ minWidth: col.minWidth }}
                        className={cn(
                          'min-w-32 overflow-hidden break-words h-11 px-3 py-2 align-top font-body text-body text-text-heading [overflow-wrap:anywhere] sm:px-4',
                          typeof getNestedValue(row, col.key) === 'number' && 'text-right',
                          columnIndex === 0 &&
                            (selectable
                              ? 'sticky left-10 z-10 bg-inherit'
                              : 'sticky left-0 z-10 bg-inherit'),
                          col.className,
                          col.technical && 'w-48 min-w-48 max-w-48',
                          (col.key === 'actions' || col.key === 'action') &&
                            'sticky right-0 z-10 bg-inherit whitespace-nowrap [&>div]:flex [&>div]:flex-nowrap [&>div]:items-center [&>div]:gap-2 [&_button]:shrink-0 [&_button]:whitespace-nowrap [&_a]:shrink-0 [&_a]:whitespace-nowrap',
                        )}
                      >
                        {col.render ? (
                          col.render(getNestedValue(row, col.key), row, globalIndex)
                        ) : col.technical && getNestedValue(row, col.key) != null ? (
                          <TechnicalValue
                            label={col.header}
                            value={String(getNestedValue(row, col.key))}
                          />
                        ) : (
                          ((getNestedValue(row, col.key) as React.ReactNode) ?? '\u2014')
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </TableScrollRegion>

      {/* Pagination */}
      {pagination ? (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border bg-surface-sheet px-4 py-3">
          <span className="font-body text-caption text-text-muted" aria-live="polite">
            {pagination.total === undefined
              ? pagination.hasMore
                ? `Page ${serverPage}, more available`
                : `Page ${serverPage}`
              : paginatedData.length === 0
                ? `Showing 0–0 of ${pagination.total}`
                : `Showing ${rowOffset + 1}–${rowOffset + paginatedData.length} of ${pagination.total}`}
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="toolbar"
              disabledReason={
                pagination.loading
                  ? 'Page is loading'
                  : serverPage === 1
                    ? 'You are on the first page'
                    : undefined
              }
              onClick={() => pagination.onPageChange(serverPage - 1)}
            >
              Previous
            </Button>
            <Button
              type="button"
              variant="outline"
              size="toolbar"
              disabledReason={
                pagination.loading
                  ? 'Page is loading'
                  : !pagination.hasMore
                    ? 'You are on the last page'
                    : undefined
              }
              onClick={() => pagination.onPageChange(serverPage + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : (
        (totalPages > 1 || (pageSizeOptions && pageSizeOptions.length > 1)) && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border bg-surface-sheet px-4 py-3">
            <span className="font-body text-caption text-text-muted">
              {totalPages > 1 ? (
                <>
                  Showing {(currentPage - 1) * pageSize + 1}–
                  {Math.min(currentPage * pageSize, sortedData.length)} of {sortedData.length}
                </>
              ) : (
                <>
                  {sortedData.length}{' '}
                  {sortedData.length === 1 ? itemLabel.singular : itemLabel.plural}
                </>
              )}
              {pageSizeOptions && pageSizeOptions.length > 1 && onPageSizeChange && (
                <>
                  {' '}
                  ·{' '}
                  <label className="inline-flex items-center gap-1">
                    <span>Rows per page</span>
                    <NativeSelect
                      value={pageSize}
                      onChange={(e) => {
                        const next = Number.parseInt(e.target.value, 10);
                        if (!Number.isFinite(next)) return;
                        onPageSizeChange(next);
                        setCurrentPage(1);
                      }}
                      aria-label="Rows per page"
                      className="min-h-11 rounded-control border border-border bg-surface-sheet px-3 py-2 font-body text-body text-text-heading focus:outline-none focus-visible:ring-2 focus-visible:ring-focus sm:min-h-9"
                    >
                      {pageSizeOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </NativeSelect>
                  </label>
                </>
              )}
            </span>
            <div className="flex flex-wrap items-center gap-1">
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-control text-text-body transition-interactive duration-200 hover:bg-surface-soft disabled:pointer-events-none disabled:opacity-40"
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((page) => {
                  if (totalPages <= 7) return true;
                  if (page === 1 || page === totalPages) return true;
                  if (Math.abs(page - currentPage) <= 1) return true;
                  return false;
                })
                .map((page, idx, arr) => {
                  const showEllipsis = idx > 0 && page - (arr[idx - 1] ?? 0) > 1;
                  return (
                    <React.Fragment key={page}>
                      {showEllipsis && (
                        <span className="px-1 font-body text-caption text-text-subtle">...</span>
                      )}
                      <button
                        type="button"
                        onClick={() => setCurrentPage(page)}
                        aria-current={page === currentPage ? 'page' : undefined}
                        aria-label={`Page ${page}`}
                        className={cn(
                          'inline-flex h-8 w-8 items-center justify-center rounded-control font-body text-caption font-semibold transition-interactive duration-200',
                          page === currentPage
                            ? 'bg-selected text-selected-foreground'
                            : 'text-text-body hover:bg-surface-soft',
                        )}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  );
                })}
              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-control text-text-body transition-interactive duration-200 hover:bg-surface-soft disabled:pointer-events-none disabled:opacity-40"
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )
      )}
    </div>
  );
}

function experienceRowEvidence(row: Record<string, unknown>, key: string) {
  const entityIds = new Set<string>();
  const add = (value: unknown) => {
    if (typeof value === 'string' && value.length > 0) entityIds.add(value);
  };
  add(key);
  for (const field of [
    'id',
    'client_id',
    'resident_id',
    'staff_id',
    'care_plan_id',
    'program_enrollment_id',
  ]) {
    add(row[field]);
  }
  const metadata =
    row.metadata && typeof row.metadata === 'object' && !Array.isArray(row.metadata)
      ? (row.metadata as Record<string, unknown>)
      : {};
  add(metadata.supersedesEnrollmentId);
  add(metadata.supersededByEnrollmentId);

  const primaryId = typeof row.id === 'string' ? row.id : key;
  const outcomeTokens: string[] = [];
  const addOutcome = (field: string, value: unknown, present: boolean) => {
    if (!present) return;
    const normalized = value === null ? 'null' : String(value);
    outcomeTokens.push(`entity:${primaryId}.${field}=${normalized}`);
  };
  for (const field of [
    'status',
    'means_testing_status',
    'means_tested_fee',
    'annual_income',
    'authority',
    'program_transition',
    'integration',
  ]) {
    addOutcome(field, row[field], Object.prototype.hasOwnProperty.call(row, field));
  }
  const customFields =
    row.custom_fields && typeof row.custom_fields === 'object' && !Array.isArray(row.custom_fields)
      ? (row.custom_fields as Record<string, unknown>)
      : {};
  addOutcome('open_matter', customFields.openMatter, 'openMatter' in customFields);
  return { entityIds: [...entityIds], outcomeTokens };
}
